import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";

// Pages
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Dashboard } from "./pages/Dashboard";
import { Campaigns } from "./pages/Campaigns";
import CampaignGroups from "./pages/CampaignGroups";
import { Reports } from "./pages/Reports";
import { ReportSchedules } from "./pages/ReportSchedules";
import { PlatformIntegrations } from "./pages/PlatformIntegrations";
import { Users } from "./pages/Users";
import { Admins } from "./pages/Admins";
import { Settings } from "./pages/Settings";
import Advertisers from "./pages/Advertisers";
import AdvertiserDetail from "./pages/AdvertiserDetail";
import { MyAccount } from "./pages/MyAccount";
import MyCampaigns from "./pages/MyCampaigns";
import CustomFields from "./pages/CustomFields";
import DataImport from "./pages/DataImport";
import AlertRules from "./pages/AlertRules";
import CustomMetrics from "./pages/CustomMetrics";
import PerformanceReport from "./pages/PerformanceReport";
import PlatformReports from "./pages/PlatformReports";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Campaigns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaign-groups"
        element={
          <ProtectedRoute>
            <CampaignGroups />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-report"
        element={
          <ProtectedRoute>
            <PlatformReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-report/:platform"
        element={
          <ProtectedRoute>
            <PerformanceReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/report-schedules"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "advertiser"]}>
            <ReportSchedules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <PlatformIntegrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advertisers"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <Advertisers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/advertisers/:id"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "advertiser", "user"]}>
            <AdvertiserDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-account"
        element={
          <ProtectedRoute allowedRoles={["advertiser", "user"]}>
            <MyAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-campaigns"
        element={
          <ProtectedRoute allowedRoles={["advertiser", "user"]}>
            <MyCampaigns />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <Admins />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <CustomFields />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-import"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <DataImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <AlertRules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-metrics"
        element={
          <ProtectedRoute allowedRoles={["super_admin", "admin"]}>
            <CustomMetrics />
          </ProtectedRoute>
        }
      />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
